import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import VisitService from "../../../services/api/visit.service";
import { VisitStatisticResponse } from "../../../models/visit";
import {
  TranslateStatisticResponse,
  TranslateStatisticSummary,
} from "../../../models/translate";
import TranslateService from "../../../services/api/translate.service";
import { EPaymentStatus } from "../../main/AiToolsPage";
import { StatisticCard } from "./StatisticCard";
import { TranslateKpiCard } from "./TranslateKpiCard";
import { TranslateStatisticType } from "../../../enum/ETranslate";

enum VisitStatisticType {
  Day = 0,
  Week = 1,
  Month = 2,
  Year = 3,
}

const translateStatuses = [
  {
    value: undefined,
    label: "Tất cả",
  },
  {
    value: EPaymentStatus.pending,
    label: "Pending",
  },
  {
    value: EPaymentStatus.success,
    label: "Thanh toán thành công",
  },
  {
    value: EPaymentStatus.translate_Processing,
    label: "Đang dịch",
  },
  {
    value: EPaymentStatus.translate_Completed,
    label: "Hoàn thành",
  },
  {
    value: EPaymentStatus.failed,
    label: "Thất bại",
  },
  {
    value: EPaymentStatus.delete,
    label: "Đã xóa",
  },
  {
    value: EPaymentStatus.free,
    label: "Miễn phí",
  },
];

const DashboardPage = () => {
  const [type, setType] = useState<VisitStatisticType>(VisitStatisticType.Day);
  const [isMobile, setIsMobile] = useState(false);

  const [data, setData] = useState<VisitStatisticResponse[]>([]);
  const [totalUserOnline, setTotalUserOnline] = useState<number>(0);
  const [deviceData, setDeviceData] = useState<
    { device: string; total: number }[]
  >([]);
  const [loading, setLoading] = useState(false);

  const [translateType, setTranslateType] = useState<TranslateStatisticType>(
    TranslateStatisticType.Day,
  );

  const [translateStatus, setTranslateStatus] = useState<number | undefined>();

  const [translateData, setTranslateData] = useState<
    TranslateStatisticResponse[]
  >([]);

  const [translateSummary, setTranslateSummary] =
    useState<TranslateStatisticSummary | null>(null);

  const [translateLoading, setTranslateLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getDeviceStatistics = async () => {
    const res = await VisitService.getDeviceStatistics();

    if (res.success) {
      setDeviceData(res.data || []);
    }
  };

  const getStatistics = async () => {
    setLoading(true);

    const res = await VisitService.getStatistics(type);

    if (res.success) {
      setData(res.data || []);
    }

    setLoading(false);
  };

  const getTranslateStatistics = async () => {
    try {
      setTranslateLoading(true);

      const res = await TranslateService.getTranslateStatistics(
        translateType,
        translateStatus,
      );

      if (res.success && res.data) {
        setTranslateData(res.data.chart || []);

        setTranslateSummary(res.data.summary || null);
      } else {
        setTranslateData([]);
        setTranslateSummary(null);
      }
    } catch (error) {
      console.error(error);

      setTranslateData([]);
      setTranslateSummary(null);
    } finally {
      setTranslateLoading(false);
    }
  };

  const getUserOnline = async () => {
    setLoading(true);
    const res = await VisitService.getUserOnline();

    if (res.success) {
      setTotalUserOnline(res.data ?? 0);
    }

    setLoading(false);
  };

  useEffect(() => {
    getTranslateStatistics();
  }, [translateType, translateStatus]);

  const formatNumber = (value?: number | null) => {
    return Number(value ?? 0).toLocaleString("vi-VN");
  };

  const formatUsd = (value?: number | null) => {
    return `$${Number(value ?? 0).toLocaleString("en-US", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    })}`;
  };

  const formatCurrency = (value?: number | null) => {
    return Number(value ?? 0).toLocaleString("vi-VN", {
      style: "currency",
      currency: "VND",
      maximumFractionDigits: 0,
    });
  };

  const totalTranslateFiles = translateSummary?.totalFiles ?? 0;

  const averageTranslateFiles =
    translateData.length > 0
      ? Math.round(totalTranslateFiles / translateData.length)
      : 0;

  const maxTranslateFiles = Math.max(
    ...translateData.map((item) => item.total),
    0,
  );

  const totalTranslateCharacters = translateData.reduce(
    (sum, item) => sum + Number(item.totalCharacters ?? 0),
    0,
  );

  const totalTranslateTokens = translateSummary?.totalTokens ?? 0;

  const totalTranslateCost = translateSummary?.costUsd ?? 0;

  const totalTranslateRevenue = translateSummary?.revenue ?? 0;

  const formatVnd = (value: number) =>
    Number(value ?? 0).toLocaleString("vi-VN") + " ₫";

  useEffect(() => {
    getStatistics();
    getUserOnline();
    getDeviceStatistics();
  }, [type]);

  const cardStyle: React.CSSProperties = {
    marginTop: "20px",
    background: "#fff",
    borderRadius: "24px",
    padding: "16px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
  };

  const filterButton = (active: boolean): React.CSSProperties => ({
    height: "42px",
    padding: "0 18px",
    borderRadius: "12px",
    border: active ? "1px solid #2563eb" : "1px solid #e5e7eb",
    background: active ? "#2563eb" : "#fff",
    color: active ? "#fff" : "#111827",
    fontWeight: 600,
    cursor: "pointer",
    transition: "0.2s",
  });

  const getTranslateChartWidth = () => {
    if (!isMobile) {
      return "100%";
    }

    switch (translateType) {
      case TranslateStatisticType.Day:
        return `${Math.max(900, translateData.length * 55)}px`;

      case TranslateStatisticType.Week:
        return `${Math.max(620, translateData.length * 70)}px`;

      case TranslateStatisticType.Month:
        return `${Math.max(1200, translateData.length * 52)}px`;

      case TranslateStatisticType.Year:
        return `${Math.max(720, translateData.length * 60)}px`;

      default:
        return `${Math.max(720, translateData.length * 55)}px`;
    }
  };

  const getVisitChartWidth = () => {
    if (!isMobile) {
      return "100%";
    }

    return `${Math.max(760, data.length * 58)}px`;
  };

  const renderMobileValueDot = (props: any) => {
    const { cx, cy, payload, value } = props;

    if (!isMobile || cx == null || cy == null) {
      return null;
    }

    const rawValue = payload?.total ?? value ?? 0;
    const numericValue = Number(rawValue);
    const displayValue = Number.isFinite(numericValue)
      ? numericValue.toLocaleString("vi-VN")
      : "0";

    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={4}
          fill="#2563eb"
          stroke="#ffffff"
          strokeWidth={2}
        />

        <text
          x={cx}
          y={cy - 12}
          textAnchor="middle"
          fill="#111827"
          fontSize={11}
          fontWeight={700}>
          {displayValue}
        </text>
      </g>
    );
  };

  const renderMobileGreenValueDot = (props: any) => {
    const { cx, cy, payload, value } = props;

    if (!isMobile || cx == null || cy == null) {
      return null;
    }

    const rawValue = payload?.total ?? value ?? 0;
    const numericValue = Number(rawValue);
    const displayValue = Number.isFinite(numericValue)
      ? numericValue.toLocaleString("vi-VN")
      : "0";

    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={4}
          fill="#16a34a"
          stroke="#ffffff"
          strokeWidth={2}
        />

        <text
          x={cx}
          y={cy - 12}
          textAnchor="middle"
          fill="#111827"
          fontSize={11}
          fontWeight={700}>
          {displayValue}
        </text>
      </g>
    );
  };

  return (
    <div
      style={{
        padding: "12px",
        background: "#f5f7fb",
        minHeight: "100vh",
      }}>
      {/* Header */}
      <div>
        <h1
          style={{
            margin: 0,
            fontSize: "32px",
            fontWeight: 800,
            color: "#111827",
          }}>
          Dashboard
        </h1>

        <p
          style={{
            marginTop: "8px",
            color: "#6b7280",
            fontSize: "15px",
          }}>
          Thống kê lượt truy cập hệ thống
        </p>
      </div>

      {/* Top cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(100%, 240px),1fr))",
          gap: "20px",
        }}>
        <div style={cardStyle}>
          <div
            style={{
              fontSize: "14px",
              color: "#6b7280",
              marginBottom: "10px",
            }}>
            Tổng lượt truy cập
          </div>

          <div
            style={{
              fontSize: "34px",
              fontWeight: 800,
              color: "#111827",
            }}>
            {data.reduce((sum, item) => sum + item.total, 0)}
          </div>
        </div>

        <div style={cardStyle}>
          <div
            style={{
              fontSize: "14px",
              color: "#6b7280",
              marginBottom: "10px",
            }}>
            Trung bình
          </div>

          <div
            style={{
              fontSize: "34px",
              fontWeight: 800,
              color: "#2563eb",
            }}>
            {data.length > 0
              ? Math.round(
                  data.reduce((sum, item) => sum + item.total, 0) / data.length,
                )
              : 0}
          </div>
        </div>

        <div style={cardStyle}>
          <div
            style={{
              fontSize: "14px",
              color: "#6b7280",
              marginBottom: "10px",
            }}>
            Cao nhất
          </div>

          <div
            style={{
              fontSize: "34px",
              fontWeight: 800,
              color: "#16a34a",
            }}>
            {Math.max(...data.map((x) => x.total), 0)}
          </div>
        </div>
      </div>

      {/* <div style={cardStyle}>
        <div
          style={{
            fontSize: "14px",
            color: "#6b7280",
            marginBottom: "10px",
          }}>
          Người dùng online
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}>
          <div
            style={{
              width: "14px",
              height: "14px",
              borderRadius: "999px",
              background: "#22c55e",
              boxShadow: "0 0 12px rgba(34,197,94,0.8)",
            }}
          />

          <div
            style={{
              fontSize: "34px",
              fontWeight: 800,
              color: "#22c55e",
            }}>
            {totalUserOnline}
          </div>
        </div>

        <div
          style={{
            marginTop: "12px",
            color: "#6b7280",
            fontSize: "13px",
          }}>
          Đang hoạt động realtime
        </div>
      </div> */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(100%, 320px),1fr))",
          gap: "20px",
        }}>
        {/* Card Online */}
        <div style={cardStyle}>
          <div
            style={{
              fontSize: "14px",
              color: "#6b7280",
              marginBottom: "10px",
            }}>
            Người dùng online
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}>
            <div
              style={{
                width: "14px",
                height: "14px",
                borderRadius: "999px",
                background: "#22c55e",
                boxShadow: "0 0 12px rgba(34,197,94,0.8)",
              }}
            />

            <div
              style={{
                fontSize: "34px",
                fontWeight: 800,
                color: "#22c55e",
              }}>
              {totalUserOnline}
            </div>
          </div>

          <div
            style={{
              marginTop: "12px",
              color: "#6b7280",
              fontSize: "13px",
            }}>
            Đang hoạt động realtime
          </div>
        </div>

        {/* Card Device */}
        <div style={cardStyle}>
          <div
            style={{
              fontSize: "14px",
              color: "#6b7280",
              marginBottom: "18px",
            }}>
            Thiết bị truy cập
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "14px",
            }}>
            {deviceData.map((item, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}>
                <span>{item.device}</span>

                <strong>{item.total}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        {/* ================= HEADER ================= */}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "16px",
            flexWrap: "wrap",
          }}>
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "22px",
                fontWeight: 700,
                color: "#111827",
              }}>
              Biểu đồ dịch tài liệu
            </h2>

            <p
              style={{
                marginTop: "6px",
                marginBottom: 0,
                color: "#6b7280",
                fontSize: "14px",
              }}>
              Theo dõi số lượng tài liệu được xử lý theo thời gian
            </p>
          </div>

          {/* ================= STATUS ================= */}

          <select
            value={translateStatus === undefined ? "" : translateStatus}
            onChange={(e) => {
              const value = e.target.value;

              setTranslateStatus(value === "" ? undefined : Number(value));
            }}
            style={{
              height: "42px",
              minWidth: "190px",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              padding: "0 14px",
              background: "#fff",
              color: "#111827",
              fontWeight: 600,
              outline: "none",
              cursor: "pointer",
            }}>
            <option value="">Tất cả trạng thái</option>

            <option value={EPaymentStatus.pending}>Chờ thanh toán</option>

            <option value={EPaymentStatus.success}>Đã thanh toán</option>

            <option value={EPaymentStatus.translate_Completed}>
              Đã dịch xong
            </option>

            <option value={EPaymentStatus.free}>Miễn phí</option>
          </select>
        </div>

        {/* ================= KPI ================= */}

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit,minmax(min(100%, 180px),1fr))",
            gap: "16px",
            marginTop: "24px",
          }}>
          <TranslateKpiCard
            title="Tổng số file"
            value={formatNumber(totalTranslateFiles)}
            description="Trong khoảng thời gian đang xem"
          />

          <TranslateKpiCard
            title="Trung bình"
            value={formatNumber(averageTranslateFiles)}
            description="File / mốc thời gian"
            color="#2563eb"
          />

          <TranslateKpiCard
            title="Cao nhất"
            value={formatNumber(maxTranslateFiles)}
            description="File / mốc thời gian"
            color="#16a34a"
          />

          <TranslateKpiCard
            title="Tổng ký tự"
            value={formatNumber(totalTranslateCharacters)}
            description="Ký tự xử lý"
          />
        </div>

        {/* ================= COST ================= */}

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit,minmax(min(100%, 250px),1fr))",
            gap: "16px",
            marginTop: "20px",
          }}>
          <TranslateKpiCard
            title="Tổng Token"
            value={formatNumber(translateSummary?.totalTokens ?? 0)}
            description="Tổng token toàn thời gian"
          />

          <TranslateKpiCard
            title="Chi phí AI"
            value={formatUsd(translateSummary?.costUsd ?? 0)}
            description="Chi phí AI toàn thời gian"
          />

          <TranslateKpiCard
            title="Doanh thu"
            value={formatVnd(translateSummary?.revenue ?? 0)}
            description="Doanh thu toàn thời gian"
            color="#16a34a"
          />
        </div>

        {/* ================= CHART HEADER ================= */}

        <div
          style={{
            marginTop: "32px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
          }}>
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: 700,
              }}>
              Số lượng file dịch
            </h3>

            <p
              style={{
                margin: "5px 0 0",
                color: "#9ca3af",
                fontSize: "13px",
              }}>
              {translateType === TranslateStatisticType.Day &&
                "Thống kê theo từng giờ hôm nay"}

              {translateType === TranslateStatisticType.Week &&
                "Thống kê từ Thứ 2 đến Chủ nhật"}

              {translateType === TranslateStatisticType.Month &&
                "Thống kê từng ngày trong tháng"}

              {translateType === TranslateStatisticType.Year &&
                "Thống kê 12 tháng trong năm"}
            </p>
          </div>

          {/* ================= DATE FILTER ================= */}

          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
            }}>
            <button
              style={filterButton(translateType === TranslateStatisticType.Day)}
              onClick={() => setTranslateType(TranslateStatisticType.Day)}>
              Theo ngày
            </button>

            <button
              style={filterButton(
                translateType === TranslateStatisticType.Week,
              )}
              onClick={() => setTranslateType(TranslateStatisticType.Week)}>
              Theo tuần
            </button>

            <button
              style={filterButton(
                translateType === TranslateStatisticType.Month,
              )}
              onClick={() => setTranslateType(TranslateStatisticType.Month)}>
              Theo tháng
            </button>

            <button
              style={filterButton(
                translateType === TranslateStatisticType.Year,
              )}
              onClick={() => setTranslateType(TranslateStatisticType.Year)}>
              Theo năm
            </button>
          </div>
        </div>

        {/* ================= AREA CHART ================= */}

        {isMobile && (
          <div
            style={{
              marginTop: "12px",
              fontSize: "12px",
              color: "#9ca3af",
            }}>
            ← Vuốt ngang để xem đầy đủ biểu đồ →
          </div>
        )}

        <div
          style={{
            width: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            marginTop: "24px",
            WebkitOverflowScrolling: "touch",
          }}>
          <div
            style={{
              width: getTranslateChartWidth(),
              height: "420px",
              minWidth: "100%",
            }}>
            {translateLoading ? (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#6b7280",
                }}>
                Đang tải dữ liệu...
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={translateData}
                  margin={{
                    top: 10,
                    right: 20,
                    left: 0,
                    bottom: 10,
                  }}>
                  <defs>
                    <linearGradient
                      id="translateColor"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.4} />

                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#e5e7eb"
                  />

                  <XAxis
                    dataKey="label"
                    tick={{
                      fontSize: 12,
                      fill: "#6b7280",
                    }}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={20}
                  />

                  <YAxis
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fontSize: 12,
                      fill: "#6b7280",
                    }}
                  />

                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) {
                        return null;
                      }

                      const item = payload[0]
                        ?.payload as TranslateStatisticResponse;

                      return (
                        <div
                          style={{
                            background: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "12px",
                            padding: "12px 14px",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                            minWidth: "190px",
                          }}>
                          {/* Ngày / tuần / tháng / năm */}
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#111827",
                              marginBottom: "10px",
                            }}>
                            {label}
                          </div>

                          {/* Số file */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "20px",
                              marginBottom: "7px",
                            }}>
                            <span
                              style={{
                                color: "#6b7280",
                                fontSize: "13px",
                              }}>
                              Số file
                            </span>

                            <strong
                              style={{
                                color: "#2563eb",
                                fontSize: "13px",
                              }}>
                              {formatNumber(item?.total ?? 0)} file
                            </strong>
                          </div>

                          {/* Doanh thu */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "20px",
                            }}>
                            <span
                              style={{
                                color: "#6b7280",
                                fontSize: "13px",
                              }}>
                              Doanh thu
                            </span>

                            <strong
                              style={{
                                color: "#16a34a",
                                fontSize: "13px",
                              }}>
                              {formatVnd(item?.revenue ?? 0)}
                            </strong>
                          </div>
                        </div>
                      );
                    }}
                  />

                  <Area
                    type="monotone"
                    dataKey="total"
                    name="Số file"
                    stroke="#2563eb"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#translateColor)"
                    dot={isMobile ? renderMobileValueDot : false}
                    activeDot={{
                      r: 6,
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ================= COST ================= */}
      </div>

      {/* Chart */}
      <div style={cardStyle}>
        {/* Filter */}
        <div
          style={{
            display: "flex",
            marginTop: "12px",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "12px",
          }}>
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "22px",
                fontWeight: 700,
              }}>
              Biểu đồ truy cập
            </h2>

            <p
              style={{
                marginTop: "6px",
                color: "#6b7280",
                fontSize: "14px",
              }}>
              Theo dõi lượng truy cập hệ thống
            </p>
          </div>

          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
            }}>
            <button
              style={filterButton(type === VisitStatisticType.Day)}
              onClick={() => setType(VisitStatisticType.Day)}>
              Theo ngày
            </button>

            <button
              style={filterButton(type === VisitStatisticType.Week)}
              onClick={() => setType(VisitStatisticType.Week)}>
              Theo tuần
            </button>

            <button
              style={filterButton(type === VisitStatisticType.Month)}
              onClick={() => setType(VisitStatisticType.Month)}>
              Theo tháng
            </button>

            <button
              style={filterButton(type === VisitStatisticType.Year)}
              onClick={() => setType(VisitStatisticType.Year)}>
              Theo năm
            </button>
          </div>
        </div>

        {/* Chart */}
        {isMobile && (
          <div
            style={{
              marginBottom: "10px",
              fontSize: "12px",
              color: "#9ca3af",
            }}>
            ← Vuốt ngang để xem đầy đủ biểu đồ →
          </div>
        )}

        <div
          style={{
            width: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
          }}>
          <div
            style={{
              width: getVisitChartWidth(),
              height: "420px",
              minWidth: "100%",
            }}>
            {loading ? (
              <div>Loading...</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data}>
                  <defs>
                    <linearGradient id="colorVisit" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.4} />

                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis dataKey="label" />

                  <YAxis />

                  <Tooltip />

                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke="#2563eb"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorVisit)"
                    dot={isMobile ? renderMobileValueDot : false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Bottom charts */}
      <div
        style={{
          marginTop: "24px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(100%, 400px),1fr))",
          gap: "24px",
        }}>
        {/* Line chart */}
        <div style={cardStyle}>
          <h3
            style={{
              marginBottom: "20px",
            }}>
            Xu hướng truy cập
          </h3>

          <div
            style={{
              width: "100%",
              overflowX: "auto",
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
            }}>
            <div
              style={{
                width: getVisitChartWidth(),
                height: "300px",
                minWidth: "100%",
              }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis dataKey="label" />

                  <YAxis />

                  <Tooltip />

                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke="#16a34a"
                    strokeWidth={3}
                    dot={isMobile ? renderMobileGreenValueDot : false}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Bar chart */}
        <div style={cardStyle}>
          <h3
            style={{
              marginBottom: "20px",
            }}>
            So sánh lượt truy cập
          </h3>

          <div
            style={{
              width: "100%",
              overflowX: "auto",
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
            }}>
            <div
              style={{
                width: getVisitChartWidth(),
                height: "300px",
                minWidth: "100%",
              }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis dataKey="label" />

                  <YAxis />

                  <Tooltip />

                  <Bar dataKey="total" fill="#f59e0b" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
